import type { CanonicalCatalogItem, ExactDecimal, MoneyValue, QuantityValue, SupplierOffer } from './models.js';

export const MISSING_MARKER = '—';

interface DecimalParts {
  whole: string;
  fraction: string;
}

function decimalParts(value: ExactDecimal): DecimalParts {
  const raw = typeof value === 'number' ? String(value) : value;
  if (!/^\d+(?:\.\d+)?$/.test(raw)) throw new Error(`Invalid non-negative decimal value: ${raw}`);
  const [whole = '0', fraction = ''] = raw.split('.');
  return { whole: whole.replace(/^0+(?=\d)/, '') || '0', fraction };
}

function groupThousands(whole: string): string {
  return whole.replace(/\B(?=(\d{3})+(?!\d))/g, '\u00a0');
}

export function formatExactDecimal(value: ExactDecimal): string {
  const { whole, fraction } = decimalParts(value);
  const trimmed = fraction.replace(/0+$/, '');
  return trimmed ? `${groupThousands(whole)},${trimmed}` : groupThousands(whole);
}

export function compareExactDecimal(left: ExactDecimal, right: ExactDecimal): number {
  const a = decimalParts(left);
  const b = decimalParts(right);
  const scale = Math.max(a.fraction.length, b.fraction.length);
  const aScaled = BigInt(`${a.whole}${a.fraction.padEnd(scale, '0')}`);
  const bScaled = BigInt(`${b.whole}${b.fraction.padEnd(scale, '0')}`);
  return aScaled < bScaled ? -1 : aScaled > bScaled ? 1 : 0;
}

export function formatMoney(value: MoneyValue): string {
  if (value.amount === null) return MISSING_MARKER;
  const amount = formatExactDecimal(value.amount);
  if (value.currency === 'RUB') return `${amount} ₽`;
  return value.currency ? `${amount} ${value.currency}` : amount;
}

export function formatQuantity(value: QuantityValue): string {
  if (value.amount === null) return MISSING_MARKER;
  return formatExactDecimal(value.amount);
}

export function formatExpectedAt(value: string | null | undefined): string | null {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return value;
  return `${match[3]}.${match[2]}.${match[1]}`;
}

export function isEligibleForLowestPrice(offer: SupplierOffer): boolean {
  const state = offer.purchase.state ?? (offer.purchase.amount === null ? 'missing_not_returned' : 'available');
  return offer.purchase.amount !== null && state === 'available' && offer.freshness === 'current';
}

export function lowestEligibleOfferId(offers: SupplierOffer[]): string | null {
  const eligible = offers.filter(isEligibleForLowestPrice);
  if (eligible.length === 0) return null;
  return eligible.reduce((lowest, offer) => {
    const current = offer.purchase.amount;
    const previous = lowest.purchase.amount;
    if (current === null || previous === null) return lowest;
    return compareExactDecimal(current, previous) < 0 ? offer : lowest;
  }).id;
}

export function defaultExpandedItemId(items: CanonicalCatalogItem[]): string | null {
  return items.length > 0 ? items[0]!.id : null;
}

export function selectExpandedItem(
  items: CanonicalCatalogItem[],
  requestedId: string | null,
): string | null {
  if (requestedId && items.some((item) => item.id === requestedId)) return requestedId;
  return defaultExpandedItemId(items);
}

export function totalCanonicalResults(items: CanonicalCatalogItem[]): number {
  return items.length;
}
